import {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IGetRowsParams,
  SortChangedEvent,
} from "ag-grid-community";
// import "ag-grid-community/styles/ag-grid.css";
// import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import { Spin } from "antd";
import _, { toInteger } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import logger, { Logger } from '../../../utils/logger';
// import "./aggrid.css";

export interface IFetchRowsParams {
  perPage: number;
  page: number;
  orderBy?: string;
  isAscending?: boolean;
}

export interface IPagination {
  perPage: number;
  page: number;
}
interface AgGridProps {
  columnDefs: ColDef[];
  rowHeight?: number;
  fetchData: (params: IFetchRowsParams) => Promise<any>;
  onRowClick?: (rowData: any) => void;
  onGridReady?: (params: GridReadyEvent) => void;
  defaultData?: any;
  totalRows?: number;
  isLoading?: boolean;
  suppressPaginationPanel?: boolean; 
  defaultPagination?: IPagination;
  onColumnOrderChanged?: (order: string[]) => void;
  onColumnWidthChanged?: (colId: string, newWidth: number) => void;
  onRowSelected: (event: any) => void;
  pagination?: boolean;
  paginationPageSize?: number;
  context?: any; 
    allowRowClickSelection?: boolean;
}

export const initialGridParams = {
  perPage: 50,
  page: 1,
};

interface ISortParams {
  isAscending?: boolean;
  orderBy?: string;
}

const AgGrid: React.FC<AgGridProps> = ({
  columnDefs,
  fetchData,
  rowHeight = 40, // Provide a default value
  onRowClick,
  defaultData,
  isLoading,
  onColumnOrderChanged,
  onColumnWidthChanged,
  totalRows,
  defaultPagination,
  onGridReady,
  onRowSelected,
  pagination = false,
  paginationPageSize = 0,
   allowRowClickSelection = false,
}) => {
  const [sortParams, setSortParams] = useState<ISortParams | null>(null);
  const agGridRef = useRef<any | null>(null);
  const gridApi = useRef<GridApi | null>(null);
  const gridParamsRef = useRef<IGetRowsParams | null>(null);

  const debouncedColumnMove = useRef(
    _.debounce((newColumnOrder) => {
      onColumnOrderChanged && onColumnOrderChanged(newColumnOrder);
    }, 500)
  ).current;



  const onRowClicked = (event: any) => {
    if (onRowClick) {
      const selectedRow = event.data;
      onRowClick(selectedRow);
    }
  };
  
 // Log loading state changes for debugging
 useEffect(() => {
  Logger.log('AgGrid loading state changed:', { 
    isLoading,
    hasData: defaultData?.length > 0,
    totalRows
  });
}, [isLoading, defaultData, totalRows]);

  const onColumnOrderChangedInternal = (event: ColumnMovedEvent) => {
    if (!event.finished) {
      const newColumnOrder = event.api
        .getColumnState()
        .map((columnState) => columnState.colId);
      if (newColumnOrder) {
        onColumnOrderChanged && debouncedColumnMove(newColumnOrder);
      }
    }
  };

  const onColumnWidthChangedInternal = (event: ColumnResizedEvent) => {
    if (!event.finished) {
      const columnId = event.column?.getColId();
      const newWidth = event.column?.getActualWidth();
      if (columnId && newWidth) {
      onColumnWidthChanged && onColumnWidthChanged(columnId, newWidth);

      }
    }
  };

  const gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: initialGridParams.perPage,
    cacheBlockSize: 100,
    maxBlocksInCache: 10,
    domLayout: "autoHeight",
    onRowClicked: onRowClicked,
    onColumnMoved: onColumnOrderChangedInternal,
    suppressPaginationPanel: true,
    onColumnResized: onColumnWidthChangedInternal,
  };

  const afterGridReady = (params: GridReadyEvent) => {

    
    gridApi.current = params.api;
    onGridReady && onGridReady(params);
    
    if (defaultPagination) {
      logger.log('Setting default pagination:', defaultPagination);
      params.api?.paginationGoToPage(defaultPagination.page);
    }
  
    // Only set up datasource if we don't have initial data
    if (!defaultData || defaultData.length === 0) {
      logger.log('Setting up grid datasource');
      // In your AgGrid setup

const dataSource = {
  getRows: async (params: IGetRowsParams) => {
    logger.log('Grid params:', params);
    
    try {
      // Get current filter state directly from Redux or props
      // Modified to use props or closure values instead of filterParamRef
      const response = await fetchData({
        perPage: params.endRow - params.startRow,
        page: Math.floor(params.endRow / (params.endRow - params.startRow)),
        orderBy: params.sortModel?.length > 0 ? params.sortModel[0].colId : undefined,
        isAscending: params.sortModel?.length > 0 ? params.sortModel[0].sort === 'asc' : undefined
      });
      
      logger.log('API Response:', response);
      if (response && response.data) {
        params.successCallback(response.data, response.totalRows);
      } else {
        params.failCallback();
      }
    } catch (error) {
      console.error('API Error:', error);
      params.failCallback();
    }
  }
};
      params.api.setGridOption("datasource", dataSource);
    } else {
      logger.log('Skipping datasource setup - using initial data');
    }
  };

  const onSortChanged = (event: SortChangedEvent) => {
    if (event.columns && event.columns.length && gridApi.current) {
      const currentCol = event.columns[event.columns.length - 1];
      const orderBy = currentCol.getColId();
      const isSorting = currentCol.isSorting();
      const isAscending = currentCol.isSortAscending();
      setSortParams(isSorting ? { orderBy, isAscending } : null);
      fetchData({ ...initialGridParams, orderBy, isAscending });
    }
  };

  useEffect(() => {
    if (agGridRef.current !== null) {
      const agGridApi = agGridRef.current?.api;
      const columnApi = agGridRef.current?.columnApi;

      for (let column of columnDefs) {
        const columnRef = columnApi?.getColumn(column.colId);
        if (column && agGridApi) {
          columnApi.setColumnWidth(columnRef, column["width"]);
        }
      }
    }
  }, [columnDefs]);

  const renderAgGrid = useMemo(() => {
    return (
      <AgGridReact
        gridOptions={gridOptions}
        columnDefs={columnDefs}
        rowHeight={rowHeight}
        rowClassRules={{ "cursor-pointer": () => true }}
        suppressPaginationPanel={false}
        onSortChanged={onSortChanged}
        getRowStyle={() => ({ background: "white" })}
        ref={agGridRef}
        pagination={true}
        maxConcurrentDatasourceRequests={1}
        cacheOverflowSize={0}
        maxBlocksInCache={1}
        onGridReady={afterGridReady}
        rowModelType={"infinite"}
        rowSelection="multiple"
suppressRowClickSelection={!allowRowClickSelection}
        onRowSelected={onRowSelected}
      />
    );
  }, [columnDefs]);

  return (
    <Spin size="large" tip="Loading..." spinning={isLoading}>
      <div className="ag-theme-alpine" style={{ width: "100%" }}>
        {renderAgGrid}
      </div>
    </Spin>
  );
};

export default AgGrid;
